import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors mb-8"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-gray-400 mb-12">Last updated: March 17, 2026</p>

        <div className="space-y-10 text-gray-300 leading-relaxed">
          {/* 1. Acceptance of Terms */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Cubemate (<a href="https://cubemate.ai" className="text-purple-400 hover:underline">cubemate.ai</a>),
              you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the platform.
              These terms constitute a legally binding agreement between you and Corestrat AI Solutions ("we", "us", "our").
            </p>
          </section>

          {/* 2. Description of Service */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
            <p>
              Cubemate is an AI-powered learning platform for coding education. Our services include:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>AI-powered chat for coding assistance and learning</li>
              <li>Code execution in multiple programming languages</li>
              <li>BookMate — AI-generated coding tutorials and guides</li>
              <li>Research tools for gathering and analyzing information</li>
              <li>Interactive dashboards and data visualizations</li>
            </ul>
            <p className="mt-3">
              We may add, modify, or discontinue features at any time to improve the learning experience.
            </p>
          </section>

          {/* 3. Account Registration */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Account Registration</h2>
            <p>
              To use Cubemate, you must create an account using Google sign-in or email-based authentication.
              You agree to:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain only one account per person</li>
              <li>Keep your login credentials secure and not share them with others</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="mt-3">
              We reserve the right to suspend or remove accounts that violate these terms.
            </p>
          </section>

          {/* 4. Credit System */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Credit System</h2>
            <p>
              Cubemate operates on a freemium credit-based model. Credits are consumed each time you interact
              with AI features such as chat, code generation, tutorials, and research.
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>Each subscription tier includes a monthly credit allocation</li>
              <li>Credits reset at the beginning of each billing cycle</li>
              <li>Unused credits do not roll over to the next month</li>
              <li>Credit consumption varies depending on the type and complexity of the interaction</li>
            </ul>
          </section>

          {/* 5. Subscription Plans */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Subscription Plans</h2>
            <p>
              Cubemate offers the following subscription tiers:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li><strong className="text-white">Basic</strong> — Free tier with limited monthly credits</li>
              <li><strong className="text-white">Pro</strong> — Expanded credits and features for regular learners</li>
              <li><strong className="text-white">Ultimate</strong> — Higher credit limits for power users</li>
              <li><strong className="text-white">Team</strong> — For organizations and groups (coming soon)</li>
            </ul>
            <p className="mt-3">
              Pricing and credit allocations are subject to change. We will provide reasonable notice before
              any pricing changes take effect. Existing subscriptions will be honored until the end of the
              current billing period.
            </p>
          </section>

          {/* 6. Payments */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Payments</h2>
            <p>
              Payments for paid subscription plans are processed securely through:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li><strong className="text-white">Razorpay</strong> — for users in India (INR)</li>
              <li><strong className="text-white">Stripe</strong> — for international users (USD)</li>
            </ul>
            <p className="mt-3">
              All payments are collected by <strong className="text-white">Corestrat AI Solutions</strong> ("Company"), the entity behind Cubemate. The Company name will appear on your bank or card statement for any transactions.
            </p>
            <p className="mt-3 font-medium text-white">Refund Policy:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>Credits that have already been consumed are non-refundable</li>
              <li>If you cancel an annual plan mid-cycle, a pro-rated refund may be issued for the unused portion at our discretion</li>
              <li>Refund requests must be submitted within 7 days of the transaction</li>
            </ul>
          </section>

          {/* 7. Code Execution */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Code Execution</h2>
            <p>
              Cubemate provides cloud-based code execution in sandboxed containers. By using this feature, you agree to the following:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>You are solely responsible for the code you submit for execution</li>
              <li>You will not submit malicious code, including but not limited to malware, viruses, or exploits</li>
              <li>You will not attempt cryptocurrency mining using our execution environment</li>
              <li>You will not launch network attacks, port scans, or any form of unauthorized access from our containers</li>
              <li>You will not attempt to escape the sandbox or access underlying infrastructure</li>
            </ul>
            <p className="mt-3">
              Violations may result in immediate account termination and may be reported to relevant authorities.
            </p>
          </section>

          {/* 8. Acceptable Use */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>Abuse, harass, or harm other users or our systems</li>
              <li>Scrape, crawl, or extract data from the platform using automated tools</li>
              <li>Reverse engineer, decompile, or disassemble any part of the platform</li>
              <li>Share, sell, or transfer your account credentials to another person</li>
              <li>Use the platform for any unlawful purpose</li>
              <li>Circumvent any rate limits, credit limits, or access controls</li>
              <li>Resell or redistribute AI-generated content as a competing service</li>
            </ul>
          </section>

          {/* 9. Intellectual Property */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Intellectual Property</h2>
            <p>
              <strong className="text-white">Your Content:</strong> You retain ownership of any original code, data, or content
              you create or upload to Cubemate.
            </p>
            <p className="mt-3">
              <strong className="text-white">Our Platform:</strong> The Cubemate platform — including its design, features,
              branding, and underlying technology — is the intellectual property of Corestrat AI Solutions.
              You may not copy, modify, or distribute any part of the platform without our written permission.
            </p>
            <p className="mt-3">
              <strong className="text-white">AI-Generated Content:</strong> Tutorials, code snippets, explanations, and other
              content generated by our AI are provided as-is for your personal learning use. You may use
              AI-generated code in your own projects.
            </p>
          </section>

          {/* 10. AI-Generated Content Disclaimer */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. AI-Generated Content Disclaimer</h2>
            <p>
              Cubemate uses artificial intelligence to generate responses, code, tutorials, and other educational content.
              While we strive for accuracy:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>AI-generated content may contain errors, inaccuracies, or outdated information</li>
              <li>Responses should not be treated as professional, legal, or financial advice</li>
              <li>You should always review and test any AI-generated code before using it in production environments</li>
              <li>We do not guarantee the correctness, completeness, or suitability of AI-generated content</li>
            </ul>
          </section>

          {/* 11. Limitation of Liability */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Limitation of Liability</h2>
            <p>
              Cubemate is provided on an "as-is" and "as-available" basis without warranties of any kind,
              whether express or implied. To the fullest extent permitted by law:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>We make no warranties regarding the reliability, availability, or accuracy of the platform</li>
              <li>We are not liable for any indirect, incidental, special, or consequential damages arising from your use of the platform</li>
              <li>Our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim</li>
              <li>We are not responsible for any loss of data, code, or progress resulting from service interruptions</li>
            </ul>
          </section>

          {/* 12. Termination */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Termination</h2>
            <p>
              We may suspend or terminate your account at our discretion if you violate these Terms of Service
              or engage in activity that harms the platform or other users. Upon termination:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1 ml-4">
              <li>Your access to Cubemate will be revoked immediately</li>
              <li>Unused credits will be forfeited and are non-refundable</li>
              <li>You may request a copy of your data within 30 days of termination</li>
            </ul>
            <p className="mt-3">
              You may also delete your account at any time through your account settings.
            </p>
          </section>

          {/* 13. Governing Law */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Governing Law</h2>
            <p>
              These Terms of Service are governed by and construed in accordance with the laws of India.
              Any disputes arising from these terms or your use of Cubemate shall be subject to the
              exclusive jurisdiction of the courts in India.
            </p>
          </section>

          {/* 14. Changes to Terms */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">14. Changes to Terms</h2>
            <p>
              We may update these Terms of Service from time to time. When we make changes, we will update
              the "Last updated" date at the top of this page and notify users through the platform or via email.
              Your continued use of Cubemate after changes are posted constitutes your acceptance of the
              revised terms.
            </p>
          </section>

          {/* 15. Contact */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">15. Contact</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-3">
              <a href="mailto:support@cubemate.ai" className="text-purple-400 hover:underline">
                support@cubemate.ai
              </a>
            </p>
            <p className="mt-2 text-gray-400">
              Corestrat AI Solutions
            </p>
          </section>
        </div>

        {/* Footer spacer */}
        <div className="mt-16 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
          &copy; 2026 Corestrat AI Solutions. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
